/**
 * Reusable field widgets for the redesigned "New skill" flow (web): shared by
 * the CV-skill wizard basics step, the CV "describe" screen, and the any-skill
 * "Write with AI" tab. Neutral tabbio-web-design styling.
 *
 * The model picker is the chat model picker's list (same catalog, brand marks
 * and lock badges); the pick persists as `Skill.recommendedModel`.
 */
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { HugeSymbol as MaterialSymbol } from '../icons/HugeSymbol';
import { fromWebFile, uploadImageAsset } from '../../lib/image-upload';
import { useAgentModels } from '../../hooks/useAgentModels';
import { AgentModelWordmark } from '../assistant-ui/AgentModelMark';
import {
  SKILL_MODEL_OPTIONS,
  skillModelLabel,
  type SkillBuilderIcon,
  type SkillModelOption,
} from './skill-builder-shared';

export const SKILL_ICON_GLYPH: Record<SkillBuilderIcon, string> = {
  doc: 'description',
  sparkle: 'auto_awesome',
  briefcase: 'work',
  grad: 'school',
  star: 'star',
};

const SKILL_ICONS: SkillBuilderIcon[] = ['doc', 'sparkle', 'briefcase', 'grad', 'star'];

/**
 * `region` is retained so callers need no change, but the builder no longer
 * renders a flag here: the treatment was rejected in review, and the catalog
 * (skillCatalogVisual) remains the place flags are drawn.
 */
export function SkillIconGlyph({ icon, size }: { icon: SkillBuilderIcon | 'flag'; region?: string; size: number }) {
  // A stored `flag` (no longer offered) or anything unknown reads as the
  // document, never the sparkle.
  const name = SKILL_ICON_GLYPH[icon as SkillBuilderIcon] ?? 'description';
  return <MaterialSymbol name={name} size={size} style={{ color: 'var(--text-secondary)' }} />;
}

// ── Model picker ──────────────────────────────────────────────────────────────

type ModelLock = 'plus' | 'pro' | null;

/**
 * The skill builder's model list IS the chat model picker's list: same
 * catalog, same brand marks, same lock badges. Lock state comes from the
 * server (`/api/agent/models`), which knows the plan; until it answers, the
 * paid tiers read as locked so a click can never select a model the API would
 * then refuse with a 403.
 */
export function ModelPicker({
  value,
  onChange,
  onUpgrade,
  suggestion,
}: {
  value: string;
  onChange: (id: string) => void;
  onUpgrade?: () => void;
  /**
   * A Pro model the AI recommended for this skill. The picker keeps a model
   * the user can actually select; this only drives the upsell banner, so the
   * suggestion is surfaced without ever showing a locked row as chosen.
   */
  suggestion?: string | null;
}) {
  const { t } = useTranslation('common');
  const { data } = useAgentModels();
  const [dismissed, setDismissed] = useState<string | null>(null);

  const lockOf = (option: SkillModelOption): ModelLock => {
    const remote = data?.models.find((model) => model.id === option.id);
    const minimumTier = remote?.minimumTier ?? option.entry.minimumTier;
    const locked = remote ? remote.locked : option.tier === 'pro';
    return locked ? (minimumTier === 'pro' ? 'pro' : 'plus') : null;
  };

  const suggested = SKILL_MODEL_OPTIONS.find((option) => option.id === suggestion);
  const upsell =
    suggested && suggested.id !== value && suggested.id !== dismissed && lockOf(suggested)
      ? suggested
      : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {SKILL_MODEL_OPTIONS.map((option) => {
        const lock = lockOf(option);
        const active = !lock && option.id === value;
        return (
          <button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={option.entry.displayName}
            onClick={() => (lock ? onUpgrade?.() : onChange(option.id))}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              width: '100%',
              padding: '12px 14px',
              borderRadius: 14,
              border: active
                ? '1.5px solid var(--text-primary)'
                : '1px solid var(--input-border)',
              background: active ? 'rgba(0,0,0,0.06)' : 'var(--input-bg)',
              cursor: 'pointer',
              fontFamily: 'inherit',
              textAlign: 'start',
              transition: 'border-color 120ms, background 120ms',
            }}
          >
            <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <AgentModelWordmark model={option.entry} size={19} maxWidth={240} />
              {/* Sits under the name, past the brand mark (19px mark + 6px gap). */}
              <span style={{ display: 'block', fontSize: 13, lineHeight: '17px', color: 'var(--text-tertiary)', marginInlineStart: 25 }}>
                {t(`chat.model.options.${option.entry.slug}.description`)}
              </span>
            </span>
            {lock ? (
              <span style={lockBadge}>{t(`chat.model.locked.${lock}`)}</span>
            ) : active ? (
              <MaterialSymbol name="check" size={18} style={{ color: 'var(--text-primary)', flexShrink: 0 }} />
            ) : null}
          </button>
        );
      })}
      {upsell ? (
        <div style={upgradeBanner}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 500 }}>
              {t('skills.newFlow.upgrade.title', { model: skillModelLabel(upsell.id) })}
            </div>
            <div style={{ fontSize: 13, opacity: 0.8, marginTop: 2 }}>
              {t('skills.newFlow.upgrade.body')}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setDismissed(upsell.id)}
            style={{ background: 'transparent', border: 0, color: 'inherit', opacity: 0.75, fontSize: 13, fontFamily: 'inherit', cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            {t('skills.newFlow.upgrade.notNow')}
          </button>
          <button
            type="button"
            onClick={() => onUpgrade?.()}
            style={{ background: 'var(--bg)', color: 'var(--text-primary)', border: 0, borderRadius: 9999, height: 34, paddingInline: 16, fontSize: 13, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            {t('skills.newFlow.upgrade.upgrade')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

// ── Icon picker ───────────────────────────────────────────────────────────────

export function SkillIconPicker({
  value,
  region,
  iconUrl,
  onChange,
  onImageChange,
}: {
  value: SkillBuilderIcon | 'flag';
  /** Drives which country flag the "flag" icon renders. */
  region: string;
  /** Uploaded custom icon image URL ('' = none). Takes precedence over the glyph. */
  iconUrl: string;
  onChange: (icon: SkillBuilderIcon) => void;
  /** Reports a newly-uploaded image URL, or '' when a preset is chosen. */
  onImageChange: (url: string) => void;
}) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setOpen(false);
    setUploading(true);
    try {
      const asset = fromWebFile(file);
      const url = await uploadImageAsset(asset, {
        kind: 'skill-icon',
        fileName: asset.name,
        mimeType: asset.type,
      });
      onImageChange(url);
    } catch {
      // Upload failed: leave the current icon unchanged.
    } finally {
      setUploading(false);
    }
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={t('skills.newFlow.basics.iconLabel')}
        style={iconTrigger}
      >
        {uploading ? (
          <MaterialSymbol name="progress_activity" size={22} style={{ color: 'var(--text-tertiary)' }} />
        ) : iconUrl ? (
          <img src={iconUrl} alt="" width={30} height={30} style={{ borderRadius: 8, objectFit: 'cover' }} />
        ) : (
          <SkillIconGlyph icon={value} region={region} size={24} />
        )}
      </button>
      {open ? (
        <div style={iconPopover}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
            {SKILL_ICONS.map((icon) => {
              const active = icon === value && !iconUrl;
              return (
                <button
                  key={icon}
                  type="button"
                  onClick={() => {
                    onImageChange('');
                    onChange(icon);
                    setOpen(false);
                  }}
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    border: active
                      ? '1.5px solid var(--text-primary)'
                      : '1px solid var(--input-border)',
                    background: active ? 'rgba(0,0,0,0.06)' : 'var(--input-bg)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <SkillIconGlyph icon={icon} region={region} size={20} />
                </button>
              );
            })}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => {
              void handleFile(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              marginTop: 10,
              background: 'transparent',
              border: 0,
              color: 'var(--text-secondary)',
              fontSize: 14,
              fontWeight: 500,
              fontFamily: 'inherit',
              cursor: 'pointer',
              padding: '4px 2px',
            }}
          >
            <MaterialSymbol name="upload" size={18} />
            {t('skills.newFlow.basics.upload')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

// ── Audience dropdown (chip trigger + popover) ─────────────────────────────────

export function AudienceDropdown({
  value,
  options,
  onChange,
  isRTL,
}: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  isRTL: boolean;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const current = options.find((o) => o.value === value) ?? options[0];

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button type="button" className="tabbio-chip" onClick={() => setOpen((o) => !o)} aria-haspopup="listbox" aria-expanded={open}>
        {current?.label}
        <MaterialSymbol name="expand_more" size={18} style={{ color: 'var(--text-secondary)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
      </button>
      {open ? (
        <div role="listbox" style={{ ...audiencePopover, insetInlineStart: 0, textAlign: isRTL ? 'right' : 'left' }}>
          {options.map((opt) => {
            const active = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  width: '100%',
                  padding: '9px 12px',
                  borderRadius: 10,
                  border: 0,
                  background: active ? 'var(--hover-strong)' : 'transparent',
                  color: 'var(--text-primary)',
                  fontSize: 14,
                  fontWeight: active ? 500 : 400,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                  textAlign: 'start',
                }}
              >
                {opt.label}
                {active ? <MaterialSymbol name="check" size={16} style={{ color: 'var(--text-primary)' }} /> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

// ── Shared field label + textarea helpers ──────────────────────────────────────

export function FieldLabel({ children }: { children: ReactNode }) {
  return <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 10 }}>{children}</div>;
}

// ── Styles ────────────────────────────────────────────────────────────────────

const lockBadge: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  padding: '3px 10px',
  borderRadius: 9999,
  background: 'rgba(0,0,0,0.07)',
  color: 'var(--text-secondary)',
  flexShrink: 0,
};

const upgradeBanner: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  marginTop: 4,
  padding: '12px 14px',
  borderRadius: 16,
  background: 'var(--text-primary)',
  color: 'var(--bg)',
};

// Same height as the wizard's text inputs (48), so the pair sits on one line.
const iconTrigger: CSSProperties = {
  width: 48,
  height: 48,
  borderRadius: 12,
  border: '1px solid var(--input-border)',
  background: 'var(--input-bg)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  flexShrink: 0,
};

const iconPopover: CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 8px)',
  insetInlineStart: 0,
  zIndex: 1000,
  padding: 12,
  borderRadius: 18,
  background: 'var(--menu-bg)',
  boxShadow: 'var(--gem-menu-shadow)',
};

const audiencePopover: CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 6px)',
  minWidth: 180,
  background: 'var(--menu-bg)',
  borderRadius: 16,
  boxShadow: 'var(--gem-menu-shadow)',
  padding: 6,
  zIndex: 1000,
};
